import { useState } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Button } from "@/components/ui/button";
import { Check, ChevronsUpDown, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { Badge } from "@/components/ui/badge";

export interface MultiSelectOption {
    label: string;
    value: string;
    badgeClassName?: string;
}

interface MultiSelectProps {
    options: MultiSelectOption[];
    value: string[];
    onChange: (values: string[]) => void;
    placeholder?: string;
    searchPlaceholder?: string;
    className?: string;
    popoverClassName?: string;
}

const MultiSelect = ({ options, value, onChange, placeholder = "Select...", searchPlaceholder = "Search...", className, popoverClassName }: MultiSelectProps) => {
    const [open, setOpen] = useState(false);

    const toggle = (val: string) => {
        onChange(value.includes(val) ? value.filter(v => v !== val) : [...value, val]);
    };

    const clear = (e: React.MouseEvent) => {
        e.stopPropagation();
        onChange([]);
    };

    return (
        <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
                <Button
                    variant="outline"
                    role="combobox"
                    aria-expanded={open}
                    className={cn("justify-between min-w-[160px] h-auto min-h-9 flex-wrap gap-1", className)}
                >
                    {value.length === 0 ? (
                        <span className="text-muted-foreground font-normal">{placeholder}</span>
                    ) : (
                        <div className="flex flex-wrap gap-1">
                            {value.map(v => {
                                const opt = options.find(o => o.value === v);
                                return (
                                    <Badge key={v} className={cn("font-normal", opt?.badgeClassName ?? "bg-secondary text-secondary-foreground hover:bg-secondary")}>
                                        {opt?.label ?? v}
                                    </Badge>
                                );
                            })}
                        </div>
                    )}
                    <div className="flex items-center gap-1 ml-1 shrink-0">
                        {value.length > 0 && (
                            <span role="button" aria-label="Clear selection" onClick={clear} className="flex text-muted-foreground hover:text-foreground">
                                <X className="h-3.5 w-3.5" />
                            </span>
                        )}
                        <ChevronsUpDown className="h-4 w-4 text-muted-foreground" />
                    </div>
                </Button>
            </PopoverTrigger>
            <PopoverContent className={cn("w-[280px] p-0", popoverClassName)} align="start">
                <Command>
                    <CommandInput placeholder={searchPlaceholder} />
                    <CommandList>
                        <CommandEmpty>No results found.</CommandEmpty>
                        <CommandGroup>
                            {options.map(option => (
                                <CommandItem
                                    key={option.value}
                                    value={option.value}
                                    onSelect={() => toggle(option.value)}
                                >
                                    <Check className={cn("mr-2 h-4 w-4", value.includes(option.value) ? "opacity-100" : "opacity-0")} />
                                    {option.badgeClassName ? (
                                        <Badge className={cn("font-normal", option.badgeClassName)}>{option.label}</Badge>
                                    ) : option.label}
                                </CommandItem>
                            ))}
                        </CommandGroup>
                    </CommandList>
                </Command>
            </PopoverContent>
        </Popover>
    );
};

export default MultiSelect;
